import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { ToastContext } from "../../../contexts/toast-context.jsx";
import { usePatchProject } from "../../../api/use-project.js";

export const ProjectSettings = ({ open, project, onClose }) => {
  const { t } = useTranslation(["project", "validation", "common"]);
  const { showSuccess } = useContext(ToastContext);

  const schema = yup.object({
    alignPhotosDownscale: yup.number(),
    alignPhotosAdaptiveFitting: yup.boolean(),
    buildPointCloudDownscale: yup.number(),
    buildPointCloudFilterMode: yup.string(),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset
  } = useForm({
    defaultValues: project.metashapeConfig,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (project && open) {
      reset(project.metashapeConfig);
    }
  }, [open, project, reset]);

  const [{ loading, error }, execute] = usePatchProject(project?.id);

  const onSubmit = async (data) => {
    const patch = { metashapeConfig: data };
    await execute({ data: patch });
    onClose(patch);
    showSuccess(t("successfulAction"));
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t("settings")}</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            render={({ field }) => (
              <>
                <InputLabel sx={{ mb: 1 }}>
                  {t("alignPhotosDownscale")}
                </InputLabel>
                <Typography
                  variant="caption"
                  sx={{ mb: 1, textAlign: "center" }}
                >
                  {t("alignPhotosDownscaleDesc")}
                </Typography>
                <ToggleButtonGroup
                  value={field.value}
                  exclusive
                  onChange={(_, x) => x != null && field.onChange(x)}
                >
                  <ToggleButton value={8}>
                    {t("downscaleOptions.low")}
                  </ToggleButton>
                  <ToggleButton value={4}>
                    {t("downscaleOptions.medium")}
                  </ToggleButton>
                  <ToggleButton value={2}>
                    {t("downscaleOptions.high")}
                  </ToggleButton>
                  <ToggleButton value={1}>
                    {t("downscaleOptions.maximum")}
                  </ToggleButton>
                </ToggleButtonGroup>
                <FormHelperText className="Mui-error">
                  {errors.alignPhotosDownscale?.message}
                </FormHelperText>
              </>
            )}
            control={control}
            name="alignPhotosDownscale"
          />
          <Controller
            render={({ field }) => (
              <>
                <InputLabel sx={{ mb: 1, mt: 2 }}>
                  {t("alignPhotosAdaptiveFitting")}
                </InputLabel>
                <Typography
                  variant="caption"
                  sx={{ mb: 1, textAlign: "center" }}
                >
                  {t("alignPhotosAdaptiveFittingDesc")}
                </Typography>
                <ToggleButtonGroup
                  value={field.value}
                  exclusive
                  onChange={(_, x) => x != null && field.onChange(x)}
                >
                  <ToggleButton value={false}>{t("disabled")}</ToggleButton>
                  <ToggleButton value={true}>{t("enabled")}</ToggleButton>
                </ToggleButtonGroup>
                <FormHelperText className="Mui-error">
                  {errors.alignPhotosDownscale?.message}
                </FormHelperText>
              </>
            )}
            control={control}
            name="alignPhotosAdaptiveFitting"
          />
          <Controller
            render={({ field }) => (
              <>
                <InputLabel sx={{ mb: 1, mt: 2 }}>
                  {t("buildPointCloudDownscale")}
                </InputLabel>
                <ToggleButtonGroup
                  value={field.value}
                  exclusive
                  onChange={(_, x) => x != null && field.onChange(x)}
                >
                  <ToggleButton value={8}>
                    {t("downscaleOptions.low")}
                  </ToggleButton>
                  <ToggleButton value={4}>
                    {t("downscaleOptions.medium")}
                  </ToggleButton>
                  <ToggleButton value={2}>
                    {t("downscaleOptions.high")}
                  </ToggleButton>
                  <ToggleButton value={1}>
                    {t("downscaleOptions.maximum")}
                  </ToggleButton>
                </ToggleButtonGroup>
                <FormHelperText className="Mui-error">
                  {errors.alignPhotosDownscale?.message}
                </FormHelperText>
              </>
            )}
            control={control}
            name="buildPointCloudDownscale"
          />
          <Controller
            render={({ field }) => (
              <>
                <InputLabel sx={{ mb: 1, mt: 2 }}>
                  {t("buildPointCloudFilterMode")}
                </InputLabel>
                <Typography
                  variant="caption"
                  sx={{ mb: 1, textAlign: "center" }}
                >
                  {t("buildPointCloudFilterModeDesc")}
                </Typography>
                <ToggleButtonGroup
                  value={field.value}
                  exclusive
                  onChange={(_, x) => x != null && field.onChange(x)}
                >
                  <ToggleButton value="Metashape.MildFiltering">
                    {t("filteringOptions.mild")}
                  </ToggleButton>
                  <ToggleButton value="Metashape.ModerateFiltering">
                    {t("filteringOptions.moderate")}
                  </ToggleButton>
                  <ToggleButton value="Metashape.AggressiveFiltering">
                    {t("filteringOptions.aggressive")}
                  </ToggleButton>
                </ToggleButtonGroup>
                <FormHelperText className="Mui-error">
                  {errors.alignPhotosDownscale?.message}
                </FormHelperText>
              </>
            )}
            control={control}
            name="buildPointCloudFilterMode"
          />
          {error && (
            <FormHelperText sx={{mt: 2}} error>
              {t("genericError", { action: t("saveProjectSettings") })}
            </FormHelperText>
          )}
        </Box>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={30} sx={{ mt: 3 }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={loading}>
          {t("cancel")}
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={loading} autoFocus>
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
